import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import { DialogContent, DialogActions, DialogTitle, Button, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import uploadAgentImage from './Avatar.api';
import { AgentInputType } from '../../../types/AgentInputType.types';
import Agentv2 from '../../../types/Agentv2.types';

interface Props {
  currentAgentAvatar: string | undefined;
  agentID: number | string | undefined | null;
}

function AgentAvatar({ currentAgentAvatar, agentID }: Props) {
  const { setFieldValue } = useFormikContext<AgentInputType | Agentv2>();
  const { request, isLoading, error: uploadError } = uploadAgentImage();
  const [open, setOpen] = useState(false);
  const [fotoToBig, setFotoToBig] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      const files = target.files || [];
      const firstImage = files[0];

      if (!firstImage) return;

      setFotoToBig(false);
      setUploadFailed(false);
      const maxPhotoSizeInByte = 10485760;

      if (firstImage.size >= maxPhotoSizeInByte) {
        setFotoToBig(true);
      } else if (agentID) {
        // Sofort lokale Vorschau anzeigen
        setLocalPreview(URL.createObjectURL(firstImage));

        request(firstImage, agentID)
          .then((result) => {
            const uploadedFile = result?.data?.uploadImage;
            if (uploadedFile) {
              setFieldValue('thumbnail', `/thumbnails?file=${uploadedFile}&t=${Date.now()}`, false);
            }
          })
          .catch(() => {
            setUploadFailed(true);
            setLocalPreview(null);
          });
      }

      // Reset input damit die gleiche Datei erneut gewählt werden kann
      // eslint-disable-next-line no-param-reassign
      target.value = '';
    },
    [agentID, request, setFieldValue],
  );

  const avatarSrc = localPreview || currentAgentAvatar;

  return (
    <>
      <Dialog open={open}>
        <DialogTitle>Berater noch nicht gespeichert</DialogTitle>
        <DialogContent>Bitte speichern Sie zuerst den Berater, bevor ein Foto hochgeladen werden kann.</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      <Tooltip title="Foto ändern">
        <Box
          display="flex"
          width="100%"
          justifyContent="center"
          alignItems="center"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (!agentID) setOpen(true);
            else document.getElementById('AgentEditAvatar')?.click();
          }}
        >
          {isLoading && (
            <Box display="flex" width={100} height={100} justifyContent="center" alignItems="center">
              <CircularProgress size="30px" />
            </Box>
          )}
          {!isLoading && (
            <Avatar
              data-testid="agentedit-avatar"
              src={avatarSrc}
              sx={{ width: 100, height: 100, '& img': { objectFit: 'cover' } }}
            />
          )}
          <input
            data-testid="agentedit-avatar-input"
            hidden
            accept="image/*"
            id="AgentEditAvatar"
            type="file"
            onChange={handleChange}
            multiple
          />
        </Box>
      </Tooltip>
      {fotoToBig && (
        <Typography align="center" color="error" variant="subtitle2">
          Bild ist zu groß (max. 10MB)
        </Typography>
      )}
      {!fotoToBig && (uploadFailed || uploadError) && (
        <Typography align="center" color="error" variant="subtitle2">
          Upload fehlgeschlagen. Bitte erneut versuchen.
        </Typography>
      )}
      {!fotoToBig && !uploadFailed && !uploadError && (
        <Typography align="center" color="textSecondary" variant="subtitle2">
          maximale Bildgröße 10MB
        </Typography>
      )}
    </>
  );
}

export default AgentAvatar;
